import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, Share2, Facebook, Twitter, Link as LinkIcon, CheckCircle2, Clock } from 'lucide-react';
import ArtistProfileView from '../components/woty/ArtistProfileView';
import VoteModal from '../components/woty/VoteModal';
import VotingCountdown from '../components/woty/VotingCountdown';
import { artistPublicImage } from '../components/woty/ArtistCard';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VOTED_MSG = {
  '1': { kind: 'ok', text: "🍷 Your vote is in! Thank you for supporting local artists and helping us select The Lokal Winery's 2026 Wine of the Year!" },
  already: { kind: 'warn', text: "You've already cast your one vote. Thank you!" },
  closed: { kind: 'warn', text: 'Sorry — voting has closed.' },
  expired: { kind: 'warn', text: 'That voting link expired. Please request a new one.' },
  invalid: { kind: 'warn', text: "That voting link wasn't valid. Please try voting again." },
};
const SUBSCRIBED_MSG = "🎉 You're on the list! Keep an eye on your inbox for exciting updates, special offers, and everything happening at The Lokal.";

const WotyArtistProfile = () => {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [artist, setArtist] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState('loading');
  const [voteOpen, setVoteOpen] = useState(false);
  const [banner, setBanner] = useState(null);
  const [subBanner, setSubBanner] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([
      axios.get(`${API}/contest/artists/public/slug/${slug}`).then((r) => r.data?.artist),
      axios.get(`${API}/contest/config`).then((r) => r.data).catch(() => null),
    ]).then(([a, config]) => {
      setArtist(a);
      setCfg(config);
      setStatus('ok');
      if (a) document.title = `${a.artworkTitle || a.name} — The Lokal 2026 Wine of the Year`;
    }).catch(() => setStatus('notfound'));
  }, [slug]);

  useEffect(() => {
    const v = params.get('voted');
    if (v && VOTED_MSG[v]) {
      setBanner(VOTED_MSG[v]);
      if (v === '1' && params.get('sub') === '1') setSubBanner(SUBSCRIBED_MSG);
      params.delete('voted');
      params.delete('sub');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const votingState = cfg?.votingState || 'open';
  const shareUrl = `${API}/contest/share/${slug}`;
  const humanUrl = `${BACKEND_URL}/wine-of-the-year/${slug}`;

  const share = (network) => {
    const title = artist ? `${artist.artworkTitle || 'Artwork'} by ${artist.name} — Vote in The Lokal 2026 Wine of the Year` : 'The Lokal 2026 Wine of the Year';
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(title);
    if (network === 'facebook') window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}`, '_blank', 'noopener');
    else if (network === 'twitter') window.open(`https://twitter.com/intent/tweet?url=${u}&text=${t}`, '_blank', 'noopener');
    else if (network === 'copy') {
      navigator.clipboard.writeText(humanUrl).then(() => toast({ title: 'Link copied!' })).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#fdfbf5' }} data-testid="woty-artist-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/wine-of-the-year" className="inline-flex items-center gap-2 text-sm font-medium mb-6" style={{ color: '#722F37' }} data-testid="artist-back">
          <ArrowLeft className="w-4 h-4" /> All artworks
        </Link>

        {banner && (
          <div
            data-testid="vote-banner"
            className="mb-6 rounded-lg px-4 py-3 text-sm font-medium"
            style={banner.kind === 'ok'
              ? { background: '#ecf7ec', color: '#1e6b2e', border: '1px solid #bfe3bf' }
              : { background: '#FFFBEA', color: '#8a6d12', border: '1px solid #f0d98a' }}
          >
            {banner.text}
          </div>
        )}

        {subBanner && (
          <div
            data-testid="subscribe-banner"
            className="mb-6 rounded-lg px-4 py-3 text-sm font-medium"
            style={{ background: '#fdf1f3', color: '#722F37', border: '1px solid #e7c3ca' }}
          >
            {subBanner}
          </div>
        )}

        {banner?.kind === 'ok' && (
          <div className="mb-6 rounded-lg px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3"
            style={{ background: '#722F37', color: '#F5EBDC' }} data-testid="postvote-holiday-cta">
            <span className="text-sm">🎁 While you're here — gift custom holiday wine to someone you love. Order by Nov 15 for Christmas!</span>
            <a href="/holiday-gifting" className="shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold" style={{ background: '#FDB913', color: '#4A2E1E' }}>
              Shop holiday gifts →
            </a>
          </div>
        )}

        {status === 'loading' && <div className="h-96 rounded-2xl bg-gray-100 animate-pulse" />}

        {status === 'notfound' && (
          <div className="text-center py-20" data-testid="artist-notfound">
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Artwork not found</h1>
            <p className="mt-3 text-gray-600">This artwork may not be published yet.</p>
            <Link to="/wine-of-the-year" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md font-bold text-gray-900" style={{ background: '#FDB913' }}>
              View the gallery
            </Link>
          </div>
        )}

        {status === 'ok' && artist && (
          <>
            <ArtistProfileView artist={artist} imageSrc={artist.hasImage ? artistPublicImage(artist.id) : ''} />

            {/* Voting + sharing panel */}
            <div className="mt-8 rounded-2xl border p-6 sm:p-8" style={{ borderColor: '#f0e4c8', background: '#fff' }} data-testid="vote-panel">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                <div>
                  <p className="inline-flex items-center gap-2 text-gray-700">
                    <Heart className="w-5 h-5" style={{ color: '#722F37' }} />
                    <span className="text-2xl font-bold" data-testid="artist-vote-count">{artist.voteCount || 0}</span>
                    <span className="text-sm text-gray-500">votes</span>
                  </p>
                  {votingState === 'open' && cfg?.votingClose && (
                    <p className="mt-1 text-xs text-gray-500">Voting closes {new Date(cfg.votingClose).toLocaleDateString('en-CA', { timeZone: 'America/Toronto', dateStyle: 'medium' })}</p>
                  )}
                </div>

                {votingState === 'open' && (
                  <button
                    onClick={() => setVoteOpen(true)}
                    data-testid="open-vote-modal"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md font-bold text-gray-900 shadow-lg hover:brightness-110 transition"
                    style={{ background: '#FDB913' }}
                  >
                    <Heart className="w-5 h-5" /> Vote for this artwork
                  </button>
                )}
                {votingState === 'scheduled' && (
                  <div className="text-sm inline-flex items-center gap-2 text-gray-600" data-testid="vote-scheduled">
                    <Clock className="w-4 h-4" /> Voting opens soon
                  </div>
                )}
                {votingState === 'closed' && (
                  <div className="text-sm inline-flex items-center gap-2 text-gray-600" data-testid="vote-closed">
                    <CheckCircle2 className="w-4 h-4" /> Voting has closed
                  </div>
                )}
              </div>

              <div className="mt-6 pt-5 border-t" style={{ borderColor: '#f3ead6' }}>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-3 inline-flex items-center gap-2"><Share2 className="w-4 h-4" /> Share this artwork</p>
                <div className="flex flex-wrap gap-2">
                  <ShareBtn onClick={() => share('facebook')} icon={<Facebook className="w-4 h-4" />} label="Facebook" testId="share-facebook" />
                  <ShareBtn onClick={() => share('twitter')} icon={<Twitter className="w-4 h-4" />} label="X / Twitter" testId="share-twitter" />
                  <ShareBtn onClick={() => share('copy')} icon={<LinkIcon className="w-4 h-4" />} label="Copy link" testId="share-copy" />
                </div>
              </div>
            </div>

            <VoteModal
              open={voteOpen}
              onClose={() => setVoteOpen(false)}
              slug={slug}
              artworkTitle={artist.artworkTitle || 'this artwork'}
              artistName={artist.name}
            />
          </>
        )}
      </div>
    </div>
  );
};

const ShareBtn = ({ onClick, icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium hover:bg-[#FFFBEA] transition"
    style={{ borderColor: '#e4d3a8', color: '#722F37' }}>
    {icon} {label}
  </button>
);

export default WotyArtistProfile;
